import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Dexie from 'dexie';
import { useLiveQuery } from 'dexie-react-hooks';

import db, { DEXIE_IS_LOADING } from '../lib/db';
import { messagesByWorkspaceAndSortOrder } from '../lib/messageStore';
import { sortULIDs } from '../lib/stringUtils';
import { useWorkspaceIdScope } from '../lib/workspaceIdScope';
import LocalTime from './messages/LocalTime';

const MessageCard = ({ message, onCopy }) => {
  const handleCopy = useCallback(() => {
    onCopy(message);
  }, [message, onCopy]);

  return (
    <div className={`rounded border p-2 ${message.isMatch ? 'border-blue-400 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}>
      <div className="space-y-1">
        <div className="flex items-center justify-between gap-4 text-2xs">
          <div className="flex items-center gap-1">
            <span className="font-medium text-gray-400">channel_id:</span>
            <span className="font-mono text-gray-800">{message.channel_id}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="font-medium text-gray-400">author_id:</span>
            <span className="font-mono text-gray-800">{message.author_id || 'null'}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-gray-800">
              {message.timestamp ? <LocalTime timestamp={message.timestamp} format={'%-m/%-d/%y %-I:%M %p'} /> : '[no timestamp]'}
            </span>
            <button onClick={handleCopy} className="rounded bg-gray-200 px-1.5 py-0.5 text-2xs font-medium text-gray-600 hover:bg-gray-300">
              COPY
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between gap-4 text-2xs">
          <div className="flex items-center gap-1">
            <span className="font-medium text-gray-400">id:</span>
            <span className="font-mono text-gray-800">{message.id}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="font-medium text-gray-400">sort_order:</span>
            <span className="font-mono text-gray-800">{message.sort_order}</span>
          </div>
        </div>

        {(message.optimistic_sort_order || message.pseudotype) && (
          <div className="flex items-center justify-between gap-4 text-2xs">
            {message.optimistic_sort_order && (
              <div className="flex items-center gap-1">
                <span className="font-medium text-gray-400">optimistic:</span>
                <span className="font-mono text-gray-800">{message.optimistic_sort_order}</span>
              </div>
            )}
            {message.pseudotype && (
              <div className="flex items-center gap-1">
                <span className="font-medium text-gray-400">pseudotype:</span>
                <span className="font-mono text-gray-800">{message.pseudotype}</span>
              </div>
            )}
          </div>
        )}

        {message.text && (
          <div className="max-h-20 overflow-y-auto rounded border border-gray-200 bg-white p-1 text-2xs text-gray-800">{message.text}</div>
        )}

        {message.data && (
          <div className="space-y-1">
            <span className="text-2xs font-medium text-gray-400">Data:</span>
            <pre className="max-h-20 overflow-x-auto rounded border border-gray-200 bg-white p-1 text-2xs text-gray-800">
              {typeof message.data === 'object' ? JSON.stringify(message.data, null, 2) : message.data}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

const LogViewerMessageBrowser = () => {
  const dbName = useMemo(() => db.name, []);

  const [searchQuery, setSearchQuery] = useState('');
  const [contextCount, setContextCount] = useState(10);
  const scrollContainerRef = useRef(null);
  const currentWorkspaceId = useWorkspaceIdScope();

  // hacky but we don't want to tie the debug viewer to the main code too deeply
  const [currentChannelId, setCurrentChannelId] = useState(null);

  // get the initial channel id from the workspace mount
  useEffect(() => {
    if (currentChannelId) return;
    const mount = document.querySelector('[data-live-journal-initial-channel-id-value]');
    const channelId = mount?.dataset.liveJournalInitialChannelIdValue;
    if (channelId) setCurrentChannelId(channelId);
  }, [currentChannelId]);

  // listen for channel change and update currentChannelId
  const activeChannelHandler = (event) => {
    if (!event.detail.channelId) return;
    setCurrentChannelId(event.detail.channelId);
  };

  useEffect(() => {
    window.addEventListener('output:activate-channel', activeChannelHandler);
    return () => {
      window.removeEventListener('output:activate-channel', activeChannelHandler);
    };
  }, []);

  const foundMessages = useLiveQuery(
    async () => {
      const query = searchQuery;
      if (!query.trim() && currentChannelId) {
        let recentMessages = await db.messages
          .where('[channel_id+sort_order]')
          .between([currentChannelId, Dexie.minKey], [currentChannelId, Dexie.maxKey])
          .reverse()
          .limit(contextCount)
          .toArray();

        recentMessages = recentMessages.sort((a, b) => sortULIDs(a.sort_order, b.sort_order));
        if (recentMessages.length > 0) recentMessages[recentMessages.length - 1].isMatch = true;
        return recentMessages.map((msg) => ({ ...msg, contextGroup: 'recent' }));
      }

      const searchTerm = query.trim();
      if (!searchTerm) return [];

      let matchedMessages = [];
      const byId = await db.messages.get(searchTerm);
      if (byId) matchedMessages.push(byId);
      const bySort = currentWorkspaceId ? await messagesByWorkspaceAndSortOrder(currentWorkspaceId, searchTerm).toArray() : [];
      matchedMessages.push(...bySort);
      matchedMessages = Array.from(new Map(matchedMessages.map((message) => [message.id, message])).values());
      if (matchedMessages.length === 0) return [];

      let allMessages = [];
      for (const matchedMessage of matchedMessages) {
        const channelId = matchedMessage.channel_id;
        const targetSortOrder = matchedMessage.sort_order;
        const channelMessages = await db.messages
          .where('[channel_id+sort_order]')
          .between([channelId, Dexie.minKey], [channelId, Dexie.maxKey])
          .toArray();

        const matchedIndex = channelMessages.findIndex((msg) => msg.sort_order === targetSortOrder);
        if (matchedIndex !== -1) {
          const startIndex = Math.max(0, matchedIndex - contextCount);
          const endIndex = Math.min(channelMessages.length - 1, matchedIndex + contextCount);
          const contextMessages = channelMessages.slice(startIndex, endIndex + 1).map((msg) => ({
            ...msg,
            isMatch: msg.sort_order === targetSortOrder || msg.id === searchTerm,
            contextGroup: matchedMessage.id,
          }));
          allMessages.push(...contextMessages);
        }
      }
      return allMessages;
    },
    [searchQuery, currentChannelId, currentWorkspaceId, contextCount],
    DEXIE_IS_LOADING,
  );

  // Effect to scroll to the focused message after results are updated
  useEffect(() => {
    if (foundMessages.length > 0 && scrollContainerRef.current) {
      // Find the first matched message
      const matchedIndex = foundMessages.findIndex((msg) => msg.isMatch);

      if (matchedIndex !== -1) {
        // Use setTimeout to ensure DOM is updated
        setTimeout(() => {
          const matchedElement = scrollContainerRef.current?.children[matchedIndex];
          if (matchedElement) matchedElement.scrollIntoView({ behavior: 'instant', block: 'center' });
        }, 100);
      }
    }
  }, [foundMessages]);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    setSearchQuery(value);
  }, []);

  const handleInputFocus = useCallback((e) => {
    e.target.select();
  }, []);

  const handleKillYourSelf = useCallback(() => {
    window.dispatchEvent(new CustomEvent('output:kill-your-self'));
  }, []);

  const handleCopyMessage = useCallback((message) => {
    navigator.clipboard.writeText(JSON.stringify(message, null, 2));
  }, []);

  return (
    <div className="flex h-full flex-col">
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="font-medium">{dbName || '[unnamed db]'}</div>

          <div className="flex items-center gap-2">
            <div
              onClick={handleKillYourSelf}
              className="cursor-pointer rounded bg-red-500 px-2 py-0.5 text-2xs font-medium text-white hover:bg-red-600"
            >
              KYS
            </div>
          </div>
        </div>

        {/* Search Input */}
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="sort_order or id"
            value={searchQuery}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            className="flex-1 rounded border border-gray-300 px-2 py-0.5 text-2xs focus:ring-0 focus:outline-none"
          />
          <input
            type="number"
            placeholder="10"
            value={contextCount}
            onChange={(e) => setContextCount(parseInt(e.target.value) || 10)}
            min="0"
            max="100"
            className="rounded border border-gray-300 px-2 py-0.5 text-2xs focus:ring-0 focus:outline-none"
          />
          <button onClick={clearSearch} className="rounded bg-gray-500 px-2 py-0.5 text-2xs font-medium text-white hover:bg-gray-600">
            CLEAR
          </button>
        </div>
      </div>

      {/* Loading */}
      {foundMessages === DEXIE_IS_LOADING && (
        <div className="flex flex-1 items-center justify-center">
          <div className="flex items-center gap-2">
            <div className="size-2 animate-bounce rounded-full bg-gray-300"></div>
            <div className="size-2 animate-bounce rounded-full bg-gray-300 [animation-delay:0.2s]"></div>
            <div className="size-2 animate-bounce rounded-full bg-gray-300 [animation-delay:0.4s]"></div>
          </div>
        </div>
      )}

      {/* Results */}
      {foundMessages !== DEXIE_IS_LOADING && foundMessages.length > 0 && (
        <div className="flex min-h-0 flex-1 flex-col space-y-2">
          <h4 className="mt-2 text-xs font-medium text-gray-700">
            {searchQuery.trim() ? (
              <>
                Showing {foundMessages.length} message(s) ({foundMessages.filter((m) => m.isMatch).length} matched,{' '}
                {foundMessages.filter((m) => !m.isMatch).length} context)
              </>
            ) : (
              <>Showing {foundMessages.length} recent messages</>
            )}
          </h4>
          <div ref={scrollContainerRef} className="min-h-0 flex-1 space-y-2 overflow-y-auto">
            {foundMessages.map((message) => (
              <MessageCard key={`${message.id}-${message.contextGroup ?? 'context'}`} message={message} onCopy={handleCopyMessage} />
            ))}
          </div>
        </div>
      )}

      {searchQuery.trim().length > 0 && foundMessages.length === 0 && (
        <div className="mt-2 text-xs text-gray-500 italic">No messages found for &ldquo;{searchQuery}&rdquo;</div>
      )}
    </div>
  );
};

export default LogViewerMessageBrowser;
